import { useQuery } from '@tanstack/react-query';

import type { DemographicStat, Location, Profile } from '../../../../entities/post/models';

import { fetchDemographics, fetchLocations, fetchProfiles } from '@/entities/insight/endpoint';
import type { InsightCommonFetchParams } from '@/entities/insight/types';

// =================================================================================================
// STEP 1: Define final (camelCase) data interfaces
// =================================================================================================

export interface PeopleInsightData {
  profiles: Profile[];
  totalProfileCount: number;
  gender: DemographicStat[];
  race: DemographicStat[];
  age: DemographicStat[];
  locations: Location[];
}

// =================================================================================================
// STEP 2 & 3: Private fetching logic (Orchestrator Pattern)
// =================================================================================================

// Specific fetcher for top profiles (no filter)
async function fetchTopProfiles(params: InsightCommonFetchParams, signal?: AbortSignal) {
  return fetchProfiles({ ...params, limit: 100 }, signal);
}

// =================================================================================================
// Orchestrator Function
// =================================================================================================

async function fetchPeopleInsight(
  params: InsightCommonFetchParams,
  signal?: AbortSignal,
): Promise<PeopleInsightData> {
  const [profilesResponse, demographicsFromApi, locationsFromApi] = await Promise.all([
    fetchTopProfiles(params, signal),
    fetchDemographics(params, signal),
    fetchLocations(params, signal),
  ]);

  // Centralized transformation logic
  return {
    profiles: profilesResponse.profiles,
    totalProfileCount: profilesResponse.count,
    gender: demographicsFromApi.gender,
    race: demographicsFromApi.race,
    age: demographicsFromApi.age,
    locations: locationsFromApi,
  };
}

// =================================================================================================
// STEP 4: Public custom hook
// =================================================================================================

export function usePeopleInsight(params: Parameters<typeof fetchPeopleInsight>[0]) {
  return useQuery({
    queryKey: [
      'people-insight',
      params.queryId,
      params.startDate,
      params.endDate,
      params.publicFilter,
    ],
    queryFn: ({ signal }) => fetchPeopleInsight(params, signal),
  });
}
